import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Wordmark } from '@/components/Wordmark'

const navItems = [
  { href: '/strategy', label: 'Strategy' },
  { href: '/track-record', label: 'Track record' },
  { href: '/invest', label: 'Investing with us' },
  { href: '/for-investors', label: 'Due diligence' },
  { href: '/resources', label: 'Resources' },
  { href: '/about', label: 'About' },
]

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + '/')
}

export function SiteHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { pathname } = useLocation()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  return (
    <header className="border-border fixed top-0 right-0 left-0 z-50 border-b bg-white">
      <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between gap-6 px-5 md:px-8">
        <Link to="/" className="text-navy text-[1.35rem] md:text-[1.5rem]" aria-label="Fairmeadow Capital, home">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-x-7 lg:flex" aria-label="Main">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              aria-current={isActive(pathname, item.href) ? 'page' : undefined}
              className={cn(
                'py-2 text-[0.9rem] transition-colors',
                isActive(pathname, item.href)
                  ? 'text-navy decoration-navy font-semibold underline decoration-1 underline-offset-[10px]'
                  : 'text-muted-foreground hover:text-navy'
              )}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="bg-navy text-navy-foreground rounded-sm px-4 py-2.5 text-[0.9rem] font-semibold transition-colors hover:bg-[#2c3f53]"
          >
            Join the investor list
          </Link>
        </nav>

        <button
          type="button"
          className="text-navy -mr-2 inline-flex h-11 w-11 items-center justify-center lg:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-nav"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
          >
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 7.5h16.5M3.75 12h16.5m-16.5 4.5h16.5" />
            )}
          </svg>
        </button>
      </div>

      <div
        id="mobile-nav"
        className={cn('border-border border-t bg-white lg:hidden', mobileMenuOpen ? 'block' : 'hidden')}
      >
        <nav className="mx-auto max-w-7xl px-5 py-3" aria-label="Main">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              aria-current={isActive(pathname, item.href) ? 'page' : undefined}
              className={cn(
                'border-border block border-b py-3.5 text-base',
                isActive(pathname, item.href) ? 'text-navy font-semibold' : 'text-foreground'
              )}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="bg-navy text-navy-foreground my-4 block rounded-sm px-4 py-3.5 text-center font-semibold"
          >
            Join the investor list
          </Link>
        </nav>
      </div>
    </header>
  )
}
